import { config as envConfig } from 'ENV_CONFIG';

import { serviceApi, appointmentDraftSelectors } from '~/api';

import {
  JRNI_QUESTION_TYPE,
  buildRawQuestion,
  formatAnswers,
  validation,
  reduxFieldHelpers,
} from './helpers';
import { isBoolean } from '~/utils';
import * as images from './images';

const actionPrefix = (actionType) => `Questions/${actionType}`;
const defaultOption = { value: '', label: '', displayName: '' };
const analyticsQuestionDefaultOption = {
  displayName: 'Please select',
  value: 'default',
  hidden: true,
  disabled: true,
};

export const SET_QUESTIONS = actionPrefix`SET_QUESTIONS`;
export const setQuestions = (questions) => ({
  type: SET_QUESTIONS,
  payload: questions,
});

export const SET_RESTORED_ANSWERS = actionPrefix`SET_RESTORED_ANSWERS`;
export const setRestoredAnswers = (answers) => ({
  type: SET_RESTORED_ANSWERS,
  payload: answers,
});

const getFormattedName = ({ price, name }) =>
  price ? `${name} (£${price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')})` : name;

const shouldAddDefaultOption = (treatDropdownQuestionAsChoice, questionType, options) =>
  (questionType === JRNI_QUESTION_TYPE.DROPDOWN &&
    !treatDropdownQuestionAsChoice &&
    options.every(({ isDefault }) => !isDefault)) ||
  questionType === JRNI_QUESTION_TYPE.RADIO_PRICE;

export const mapQuestionOptions = (
  { options, type },
  { treatDropdownQuestionAsChoice, analyticsQuestion },
) => {
  if (options.length !== 0) {
    const mappedOptions = [
      ...options.map((option) => ({
        id: option.id,
        name: option.name,
        description: option.description,
        displayName: getFormattedName(option),
        price: option.price,
        isDefault: !!option.default,
        imageSrc: option.imageTitle && images[option.imageTitle],
        disabled: option.disabled,
        hidden: option.hidden,
        value: option.value,
        message: option.message,
      })),
    ];
    if (analyticsQuestion) return [analyticsQuestionDefaultOption, ...mappedOptions];
    if (shouldAddDefaultOption(treatDropdownQuestionAsChoice, type, mappedOptions))
      return [defaultOption, ...mappedOptions];
    return mappedOptions;
  }
  return options;
};

export const getDefaultValueFromOptions = (options) => {
  const defaultValue = options.length !== 0 && options.find((option) => option.default);

  return (defaultValue && getFormattedName(defaultValue)) || null;
};

const getOverriddenAttributes = (question, mappedOptions, overrideType) => {
  const questionType = overrideType || question.type;
  switch (questionType) {
    case JRNI_QUESTION_TYPE.CHECKBOX:
    case JRNI_QUESTION_TYPE.CHECKBOX_PRICE:
      return {
        title: '',
        label: getFormattedName(question),
        defaultValue: [!!parseInt(question.defaultValue, 10)],
      };
    case JRNI_QUESTION_TYPE.DROPDOWN_CHECKBOX:
      return {
        label: question.options[0].name,
        formatAnswer: formatAnswers.formatDropdownCheckboxAnswer,
      };
    case JRNI_QUESTION_TYPE.DATE_INPUT:
      return {
        defaultValue: undefined,
      };
    case JRNI_QUESTION_TYPE.IMAGE_SELECT:
    case JRNI_QUESTION_TYPE.IMAGE_SELECT_VERTICAL:
    case JRNI_QUESTION_TYPE.IMAGE_SELECT_SMALL:
      return {
        formatAnswer: formatAnswers.formatArrayWithColourAnswer(mappedOptions),
      };
    default:
      return {};
  }
};

const getInlineError = (question, inlineErrorMessagingQuestions) => {
  if (inlineErrorMessagingQuestions) {
    return inlineErrorMessagingQuestions.find((el) => question.id === el.id) || {};
  }
  return {};
};

export const getValidation = (question, inlineErrorMessagingQuestions) => {
  const inlineError = getInlineError(question, inlineErrorMessagingQuestions);

  const required = (value) => validation.requiredWithMessage(value, inlineError.message);

  const validate = inlineError.validate ? inlineError.validate : [];

  const validateWithState = inlineError.validateWithState && [
    ...validate,
    ...inlineError.validateWithState(),
  ];

  // Filter out any non-function values to prevent "o is not a function" error in final-form
  const validators = validateWithState || inlineError.validate || [required];
  return Array.isArray(validators) ? validators.filter((v) => typeof v === 'function') : validators;
};

const getReduxFieldConfig = (question, { inlineErrorMessagingQuestions }) => {
  const validationList = [...getValidation(question, inlineErrorMessagingQuestions)];
  const initialReduxFieldConfig = {
    name: `q${question.id}`,
    validate: question.required ? validationList : [],
    format: reduxFieldHelpers.format,
    parse: reduxFieldHelpers.parse,
  };

  switch (question.type) {
    case JRNI_QUESTION_TYPE.CHECKBOX:
    case JRNI_QUESTION_TYPE.CHECKBOX_PRICE:
    case JRNI_QUESTION_TYPE.DROPDOWN_CHECKBOX:
      return {
        ...initialReduxFieldConfig,
        validate: question.required ? [validation.requiredCheckbox] : [],
        format: reduxFieldHelpers.formatCheckbox,
        parse: reduxFieldHelpers.parseCheckbox,
      };
    case JRNI_QUESTION_TYPE.HEIGHT:
      return {
        ...initialReduxFieldConfig,
        validate: [validation.requiredHeight],
        format: reduxFieldHelpers.formatValueToString({}),
        parse: reduxFieldHelpers.parseObject,
      };
    case JRNI_QUESTION_TYPE.CHECKBOXES:
      return {
        name: `q${question.id}`,
        validate: question.required ? [validation.required] : [],
        format: reduxFieldHelpers.formatValueToString([]),
        parse: reduxFieldHelpers.parseMultiSelect,
      };
    case JRNI_QUESTION_TYPE.SELECT_MULTI:
      return {
        name: `q${question.id}`,
        validate: [validation.requiredMultiply],
        format: reduxFieldHelpers.formatValueToString([]),
        parse: reduxFieldHelpers.parseMultiSelect,
      };
    case JRNI_QUESTION_TYPE.DATE_INPUT:
      return {
        ...initialReduxFieldConfig,
        validate: [...initialReduxFieldConfig.validate, validation.validateDate],
      };
    case JRNI_QUESTION_TYPE.DROPDOWN:
      return {
        ...initialReduxFieldConfig,
        validate: question.required
          ? [validation.requiredDropdown]
          : initialReduxFieldConfig.validate,
      };
    default:
      return initialReduxFieldConfig;
  }
};

const getDefaultValue = ({ defaultValue, options }) => {
  const value = defaultValue || isBoolean(defaultValue) || getDefaultValueFromOptions(options);
  return value ? [value] : null;
};

export const mapChildren = (children, config) =>
  children
    ? children.map(({ question, displayCondition }) => {
        const mappedOptions = mapQuestionOptions(question, config);
        return {
          ...buildRawQuestion(question.type, config.treatDropdownQuestionAsChoice),
          id: question.id,
          reduxFieldConfig: getReduxFieldConfig(question, config),
          description: question.helpText,
          title: question.name,
          optional: !question.required,
          defaultValue: getDefaultValue(question),
          options: mappedOptions,
          displayCondition,
          ...getOverriddenAttributes(question, mappedOptions),
        };
      })
    : [];

export const mapQuestion = (question, config) => {
  const options = mapQuestionOptions(question, config);

  // Transform eye color questions to use IMAGE_SELECT_SMALL type
  const isEyeColorQuestion =
    question.id &&
    question.id.toLowerCase().includes('eye') &&
    question.id.toLowerCase().includes('colour');

  let questionType = question.type;
  let enhancedOptions = options;

  if (isEyeColorQuestion && options.length > 0) {
    // Force this to be an image select question
    questionType = JRNI_QUESTION_TYPE.IMAGE_SELECT_SMALL;

    // Add imageSrc to options based on displayName if not already present
    enhancedOptions = options.map((option) => {
      if (option.imageSrc) return option; // Already has image

      // Map displayName to imageTitle
      const imageTitle = option.displayName ? option.displayName.toLowerCase() : '';
      const imageSrc = images[imageTitle];

      return {
        ...option,
        imageSrc: imageSrc || option.imageSrc,
      };
    });
  }

  return {
    ...buildRawQuestion(questionType, config.treatDropdownQuestionAsChoice),
    id: question.id,
    reduxFieldConfig: getReduxFieldConfig(question, config),
    title: question.name,
    description: question.helpText,
    instruction: question.instruction,
    section: question.section,
    optional: !question.required,
    defaultValue: getDefaultValue(question),
    options: enhancedOptions,
    children: mapChildren(question.children, config),
    ...getOverriddenAttributes(question, enhancedOptions, questionType),
    optionsLimit: question.optionsLimit,
    imageUrl: question.imageUrl,
  };
};

const mapQuestions = (questions, getState) => {
  const {
    services: {
      config: { treatDropdownQuestionAsChoice },
    },
  } = getState();

  return {
    RETRIEVED_QUESTIONS: questions
      .filter(({ id }) =>
        envConfig.excludedQuestionsIds ? !envConfig.excludedQuestionsIds.includes(id) : true,
      )
      .map((question) =>
        mapQuestion(question, {
          treatDropdownQuestionAsChoice,
          inlineErrorMessagingQuestions: envConfig.inlineErrorMessagingQuestions,
        }),
      ),
  };
};

export function getQuestions(appointmentTypeId, postcode) {
  return serviceApi.fetchJSON(
    `appointment-types/${appointmentTypeId}/questions${postcode ? `?postcode=${postcode}` : ''}`,
    {
      method: 'GET',
    },
  );
}

const loadQuestions = (appointmentTypeId) => (dispatch, getState) => {
  const postcode = appointmentDraftSelectors.selectDraftAddress(getState())?.postcode;
  return getQuestions(appointmentTypeId, postcode).then(({ questions }) => {
    dispatch(setQuestions(mapQuestions(questions, getState)));
  });
};

const loadQuestionsForShop = (shopId, appointmentTypeId) => (dispatch, getState) =>
  serviceApi
    .fetchJSON(`stores/${shopId}/questions?appointmentTypeId=${appointmentTypeId}`, {
      method: 'GET',
    })
    .then(({ questions }) => {
      dispatch(setQuestions(mapQuestions(questions, getState)));
    });

export const retrieveQuestions = (appointmentTypeId) => loadQuestions(appointmentTypeId);

export const retrieveQuestionsForShop = (shopId, appointmentTypeId) =>
  loadQuestionsForShop(shopId, appointmentTypeId);

export const questionsActions = {
  setQuestions,
  setRestoredAnswers,
  retrieveQuestions,
  retrieveQuestionsForShop,
  getQuestions,
};
